
import React, { useState, useEffect, useCallback } from 'react';
import { Calls as API, Customers } from '../services/api';
import { useAuth } from '../context/AuthContext';

const OUTCOMES = ['answered', 'not_answered', 'voicemail', 'callback_later'];
const OUT_COLOR = {
  answered: 'var(--green)', not_answered: 'var(--red)',
  voicemail: 'var(--yellow)', callback_later: 'var(--accent)',
};
const EMPTY_FORM = { customer_id: '', phone_number: '', call_type: 'outgoing',
  outcome: 'answered', duration_seconds: '', notes: '',
  call_at: new Date().toISOString().slice(0, 16),
  next_followup_date: '' };

export default function Calls() {
  const { user }  = useAuth();
  const [list, setList]         = useState([]);
  const [custs, setCusts]       = useState([]);
  const [busy, setBusy]         = useState(true);
  const [showNew, setShowNew]   = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');
  const [search, setSearch]     = useState('');

  const load = useCallback(() => {
    setBusy(true);
    Promise.all([API.list(), Customers.list()])
      .then(([calls, customers]) => { setList(calls); setCusts(customers); })
      .catch(console.error)
      .finally(() => setBusy(false));
  }, []);
  useEffect(load, [load]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const selectCustomer = e => {
    const id   = e.target.value;
    const cust = custs.find(c => String(c.id) === id);
    setForm(f => ({ ...f, customer_id: id, phone_number: cust?.number || f.phone_number }));
  };

  const logCall = async () => {
    if (!form.customer_id || !form.phone_number) { setErr('Customer and phone number required'); return; }
    setSaving(true); setErr('');
    try {
      await API.log({ ...form, corp_id: user.corp_id, user_id: user.id, customer_id: +form.customer_id });
      setShowNew(false);
      setForm(EMPTY_FORM);
      load();
    } catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  };

  const custName = id => custs.find(c => c.id == id)?.company || `Customer ${id}`;

  const filtered = list.filter(c =>
    !search ||
    custName(c.customer_id).toLowerCase().includes(search.toLowerCase()) ||
    (c.phone_number || '').includes(search)
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">📞 Call Log</div>
        <input className="search-bar" placeholder="Search customer, phone…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <button className="btn btn-primary" onClick={() => { setShowNew(true); setErr(''); }}>+ Log Call</button>
      </div>

      {busy ? <div className="spinner-wrap"><div className="spinner" /></div> :
        filtered.length === 0 ? <div className="empty">No call logs yet</div> :
        filtered.map(c => (
          <div key={c.id} className="list-row" style={{ marginBottom: '.5rem' }}>
            <div className="row-main">
              <div className="row-title">
                {custName(c.customer_id)}
                {' · '}
                <a href={`tel:${c.phone_number}`} style={{ color: 'var(--accent)' }}>{c.phone_number}</a>
              </div>
              <div className="row-sub">
                {c.call_type} · {c.call_at?.slice(0, 16)}
                {c.duration_seconds > 0 ? ` · ${c.duration_seconds}s` : ''}
              </div>
              {c.notes && <div style={{ color: 'var(--faint)', fontSize: '.78rem', marginTop: 2 }}>{c.notes}</div>}
              {c.next_followup_date && (
                <div style={{ fontSize: '.78rem', marginTop: 4, color: 'var(--accent)' }}>
                  📅 Follow-up logged for {c.next_followup_date}
                  <span style={{ color: 'var(--faint)', marginLeft: 6 }}>· track it on the Follow-ups page</span>
                </div>
              )}
            </div>
            <div className="row-actions">
              <span className="badge" style={{ background: OUT_COLOR[c.outcome] || 'var(--faint)', color: '#0f172a' }}>
                {c.outcome?.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        ))
      }

      {/* Log Call Modal */}
      {showNew && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowNew(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Log Call</span>
              <button className="btn-icon" onClick={() => setShowNew(false)}>✕</button>
            </div>
            {err && <div className="alert alert-error">{err}</div>}
            <label style={{ marginBottom: '1rem' }}>
              Customer *
              <select value={form.customer_id} onChange={selectCustomer}>
                <option value="">-- Select Customer --</option>
                {custs.map(c => <option key={c.id} value={c.id}>{c.company} — {c.name}</option>)}
              </select>
            </label>
            <label style={{ marginBottom: '1rem' }}>
              Phone Number *
              <input type="tel" value={form.phone_number} onChange={set('phone_number')} placeholder="+91 99999 99999" />
            </label>
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>
                Call Type
                <select value={form.call_type} onChange={set('call_type')}>
                  <option value="outgoing">Outgoing</option>
                  <option value="incoming">Incoming</option>
                  <option value="missed">Missed</option>
                </select>
              </label>
              <label>
                Outcome
                <select value={form.outcome} onChange={set('outcome')}>
                  {OUTCOMES.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>Date & Time<input type="datetime-local" value={form.call_at} onChange={set('call_at')} /></label>
              <label>Duration (seconds)<input type="number" value={form.duration_seconds} onChange={set('duration_seconds')} min="0" /></label>
            </div>
            <label style={{ marginBottom: '1rem' }}>
              Notes
              <textarea rows={2} value={form.notes} onChange={set('notes')} />
            </label>
            <label style={{ marginBottom: '1.2rem' }}>
              Next Follow-up Date <span style={{ color: 'var(--faint)', fontWeight: 400 }}>(optional)</span>
              <input type="date" value={form.next_followup_date} onChange={set('next_followup_date')} />
            </label>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={logCall} disabled={saving}>{saving ? 'Saving…' : 'Save Call'}</button>
              <button className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
